import { getAtividades } from './activities';
import { getIdentificacao } from './partners';
import {
  getObjetivosGeraisHTML,
  getObjetivosEspecificosHTML,
  getJustificativaHTML,
  getResponsabilidadesUFCHTML,
  getResponsabilidadesParceiroHTML,
  getAtividadesConjuntasHTML,
  getDireitosPIHTML
} from './editor';
import { getObjetoProjeto } from './object';
import { getOrcamento, getTotalOrcamento } from './budget';
import { carregarRascunho } from './db';
import { getMeios, getMeiosUfc } from './meios';
import { getRecursosFinanceirosHTML, getInfraestruturaHTML } from './editor';
import { Action, DraftData, SectionAssistRequest, SectionAssistResponse } from './types';

function resolveApiBase(): string {
  const envBase = (import.meta as any)?.env?.VITE_API_URL as string | undefined;
  if (envBase && envBase.trim()) {
    return envBase.replace(/\/+$/, '') + '/api';
  }
  if (import.meta.env.DEV) {
    return '/api';
  }
  const origin = window.location.origin.replace(/\/+$/, '');
  return `${origin}/assis-ia/api`;
}

const API_BASE = resolveApiBase();
const ENDPOINT_DOCX = '/gerar-docx';

function fmtBRL(n: number): string {
  const v = Number.isFinite(n) ? n : 0;
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
  } catch {
    return `R$ ${v.toFixed(2).replace('.', ',')}`;
  }
}

function toNum(x: unknown): number {
  if (typeof x === 'number' && Number.isFinite(x)) return x;
  const s = String(x ?? '').replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

export async function construirPayloadPlano(): Promise<any> {
  const identificacao = getIdentificacao();
  const objeto = getObjetoProjeto();
  const atividades = getAtividades();
  const draft = await carregarRascunho().catch(() => null);

  const somaItens = (t: any) =>
    (t?.itens || []).reduce((s: number, it: any) => {
      const q = toNum(it?.quantidade);
      const vu = toNum(it?.valorUnitario);
      const vt = toNum((it as any)?.valorTotal) || q * vu;
      return s + vt;
    }, 0);

  const getTituloGrupo = (t: any): string | null =>
    (t?.tituloGrupo ?? t?.grupoTitulo ?? t?.secaoTitulo ?? null);

  const isTabelaSecao6 = (t: any): boolean => {
    if (!t) return false;
    const titulo = (getTituloGrupo(t) || '').toUpperCase();
    const nome = String(t?.nomeDespesa ?? '').toUpperCase();
    return `${titulo} ${nome}`.includes('DESPESAS OPERACIONAIS ADMINISTRATIVAS');
  };

  const tabelasFromDraft = draft?.tabelas_orcamento || [];
  const tabelasNormais = tabelasFromDraft.filter((t: any) => !isTabelaSecao6(t));
  const tabelasSecao6 = tabelasFromDraft.filter((t: any) => isTabelaSecao6(t));
  const totalSecao6Num = tabelasSecao6.reduce(
    (acc: number, t: any) => acc + somaItens(t),
    0
  );

  const subtotalBaseNum = tabelasNormais.reduce((acc: number, t: any) => acc + somaItens(t), 0);
  const totalNum = subtotalBaseNum + totalSecao6Num;

  const formatarTabela = (t: any) => {
    const tituloGrupo = String(getTituloGrupo(t) || '').trim();
    const nomeDespesaOriginal = String(t?.nomeDespesa || '').trim();

    const tituloUpper = tituloGrupo.toUpperCase();
    const nomeUpper = nomeDespesaOriginal.toUpperCase();

    const isSecao6 =
      `${tituloUpper} ${nomeUpper}`.includes('DESPESAS OPERACIONAIS ADMINISTRATIVAS');


    return {
      nomeDespesa: '',
      tituloGrupo,
      itens: (t.itens || []).map((it: any) => {
        const hasRHFields =
          it?.nome || it?.identificacao || it?.email || it?.fone || it?.funcao ||
          it?.horas || it?.formaPagamento || it?.forma || it?.metas;

        const baseItem = {
          bolsa: hasRHFields ? (it.nome || it.bolsa || '') : (it.bolsa || ''),
          unidade: it.unidade || '',
          quantidade: toNum(it.quantidade),
          valorUnitario: fmtBRL(toNum(it.valorUnitario)),
          valorTotal: fmtBRL(
            toNum(it.valorTotal) || toNum(it.quantidade) * toNum(it.valorUnitario)
          ),
        };

        if (!hasRHFields) return baseItem;

        return {
          ...baseItem,
          nome: it.nome || '',
          identificacao: it.identificacao || '',
          email: it.email || '',
          fone: it.fone || '',
          funcao: it.funcao || '',
          horas: toNum(it.horas),
          formaPagamento: it.formaPagamento || '',
          forma: it.forma || '',
          metas: it.metas || '',
        };
      }),
      subtotal: isSecao6
        ? fmtBRL(somaItens(t))
        : (t.subtotal || fmtBRL(somaItens(t))),
    };
  };

  const tabelasFormatadas = tabelasNormais.map(formatarTabela);

  const itensSecao6 = (tabelasSecao6[0]?.itens || []).map((it: any) => ({
    descricao: String(it?.bolsa || '').trim(),
    percentual: toNum(it?.quantidade),
    valorTotal: fmtBRL(toNum(it?.valorTotal)),
  }));

  const tituloSecao6 =
    String(getTituloGrupo(tabelasSecao6[0]) || '').trim() ||
    '9.6. DESPESAS OPERACIONAIS ADMINISTRATIVAS';

  const totalSecao6Fmt = fmtBRL(totalSecao6Num);

  const meiosUfcFromOrcamento = tabelasNormais
    .filter((t: any) => {
      const tituloGrupo = String(getTituloGrupo(t) || '').toUpperCase();
      return tituloGrupo.startsWith('9.1. RECURSOS HUMANOS');
    })
    .flatMap((t: any) =>
      (t.itens || []).map((it: any) => ({
        nome: it?.nome || '',
        identificacao: it?.identificacao || '',
        email: it?.email || '',
        fone: it?.fone || '',
        funcao: it?.funcao || '',
        horas: toNum(it?.horas),
        forma: it?.forma || it?.formaPagamento || '',
        formaPagamento: it?.formaPagamento || '',
        quantidade: toNum(it?.quantidade),
        valorUnitario: fmtBRL(toNum(it?.valorUnitario)),
        valorTotal: fmtBRL(
          toNum(it?.valorTotal) || toNum(it?.quantidade) * toNum(it?.valorUnitario)
        ),
        metas: it?.metas || '',
      }))
    )
    .filter((it: any) =>
      it.nome || it.identificacao || it.email || it.fone || it.funcao ||
      it.horas || it.forma || it.quantidade || it.valorUnitario || it.valorTotal || it.metas
    );

  return {
    ...identificacao,
    ...objeto,
    resultados: atividades.map((a: any) => ({ meta: a.nome, indicador: a.valor })),
    equipes: getMeios(),
    meios: getMeios(),
    meiosUfc: meiosUfcFromOrcamento,
    objetivosGerais: getObjetivosGeraisHTML(),
    objetivosEspecificos: getObjetivosEspecificosHTML(),
    justificativa: getJustificativaHTML(),
    responsabilidadesUFC: getResponsabilidadesUFCHTML(),
    responsabilidadesParceiro: getResponsabilidadesParceiroHTML(),
    atividadesConjuntas: getAtividadesConjuntasHTML(),
    direitosPI: getDireitosPIHTML(),
    recursosFinanceiros: getRecursosFinanceirosHTML(),
    infraestrutura: getInfraestruturaHTML(),
    tabelas_orcamento: tabelasFormatadas,
    totalOrcamento: fmtBRL(totalNum),
    tituloSecao: tituloSecao6,
    itens: itensSecao6,
    totalSecao: totalSecao6Fmt,
    totalOrcamentoParcial: fmtBRL(subtotalBaseNum)
  };
}

export async function handleGerarDocumento() {
  try {
    const plano = await construirPayloadPlano();
    const payloadDocx = {
      ...plano,
      dados: getAtividades(),
    };

    const url = `${API_BASE}${ENDPOINT_DOCX}`;

    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payloadDocx),
    });

    if (!resp.ok) throw new Error(`Erro ${resp.status} ao gerar documento.`);

    const blob = await resp.blob();
    const link = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = link;
    a.download = `plano-de-trabalho-${Date.now()}.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(link);
  } catch (e) {
    console.error(e);
    alert('Erro ao gerar documento. Verifique a conexão com o servidor.');
  }
}

export async function handleAssistirCampo(action: Action, campo: keyof DraftData): Promise<string | null> {
  try {
    const planoCompleto = await construirPayloadPlano();

    const payloadIA: SectionAssistRequest = {
      action,
      section_key: campo,
      plan: planoCompleto,
      context: { locale: "pt-BR" }
    };

    const urlIA = `${API_BASE}/v1/assist/section`;
    console.log('Enviando payload para IA:', urlIA);

    const response = await fetch(urlIA, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payloadIA)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail?.message || "Erro na resposta da IA");
    }

    const result: SectionAssistResponse = await response.json();
    return result.suggestions?.[0]?.new_text || null;

  } catch (error) {
    console.error("Erro na assistência da IA:", error);
    throw error;
  }
}